import { defineCustomElement } from '~/lit/define-custom-element';
import moment from 'moment';
import { LitEntity, html, css, renderIf } from '../../utility/lit-entity';
import { sharedStyles } from '../../styles/shared-styles';
import '../../common/bn-icon';
import { isMonitoredWithNoDevice } from '~/domain/bins/bin-device-state';
import {
  NO_DEVICE_BADGE_PATH,
  NO_DEVICE_ICON_VIEW_BOX,
  NO_DEVICE_SIGNAL_PATH,
  NO_DEVICE_X_PATH,
} from '~/shared/icons/no-device-paths';
import './bin-latest-level';
import './bin-profile-svg';

function renderNoDeviceIcon() {
  return html`
    <svg class='no-device-icon' viewBox=${NO_DEVICE_ICON_VIEW_BOX} xmlns='http://www.w3.org/2000/svg'>
      <path fill-rule='evenodd' clip-rule='evenodd' d=${NO_DEVICE_SIGNAL_PATH} fill='var(--gray-40)'></path>
      <path d=${NO_DEVICE_BADGE_PATH} fill='var(--red-20)'></path>
      <path d=${NO_DEVICE_X_PATH} fill='var(--gray-20)'></path>
    </svg>
  `;
}

class BinProfile extends LitEntity {
  constructor() {
    super();
    this.padding = 3;
    this.additionalLegHeight = 3;
    this.isStale = false;
    this.isOld = false;
    this.level = undefined;
    this.levelVolume = undefined;
  }

  static get properties() {
    return {
      basic: { type: Boolean },
      hideRings: { type: Boolean },
      nodevice: { type: Boolean, reflect: true },
      volumes: { type: Array }, // manually set volumes to render
      height: { type: Number },
      width: { type: Number },
      padding: { type: Number },
      additionalLegHeight: { type: Number, attribute: 'additional-leg-height' },
      // latest bin level
      ignoreLatestLevel: { type: Boolean, attribute: 'ignore-latest-level' },
      level: { type: Object },
      levelVolume: { type: Number, attribute: false },
      levelfreshnessdate: { type: Object },
      isStale: { type: Boolean, attribute: false },
      isOld: { type: Boolean, attribute: false },
      medicated: { type: Boolean },
    };
  }

  static get styles() {
    return [sharedStyles, css`
      :host{
        display: block;
        position: relative;
        height: 100%;
        width: 100%;
      }
      .no-data {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
        width: 100%;
        background-color: var(--gray-50);
        box-sizing: border-box;
        box-shadow: inset 0 0 3px var(--gray-70);
        color: var(--blue-50);
      }
      .no-data .warning {
        color: var(--red-20);
        font-weight: bold;
        z-index: 11;
        text-align: center;
        line-height: 1.5em;
      }
      .no-data .warning bn-icon {
        color: var(--red-20);
      }
      .no-data bn-icon {
        color: var(--gray-60);
        font-size: 1.5em;
      }
      .no-device {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
        width: 100%;
        overflow: hidden;
        background-color: var(--gray-10);
      }
      .no-device .no-device-icon {
        width: min(3.25rem, 60%);
        height: auto;
        max-height: 60%;
        aspect-ratio: 31 / 37.6;
      }
      .no-data.overlay {
        position: absolute;
        background: none;
        z-index: 10;
      }
      .no-data.overlay .background {
        position: absolute;
        opacity: .8;
        height: 100%;
        width: 100%;
        background-color: var(--gray-20);
      }
    `];
  }

  render() {
    if (this.loading) {
      return html`
        <div class="no-data">
          <bn-icon name="fas fa-spinner fa-pulse"></bn-icon>
        </div>
      `;
    }

    if (this.nodevice) {
      return html`
        <div class="no-device">
          ${renderNoDeviceIcon()}
        </div>
      `;
    }

    return html`
      ${renderIf(this.isStale && !this.isOld, () => html`
        <div class="no-data overlay">
          <div class="background"></div>
          <p class="warning">
            <bn-icon name="fas fa-exclamation-circle"></bn-icon><br/>
            No reading
          </p>
        </div>
      `)}
      <bin-profile-svg
        ?basic="${this.basic}"
        .showUnknownStyle="${this.isOld}"
        ?hiderings="${this.hiderings}"
        additional-leg-height="${this.additionalLegHeight}"
        .binvolumes="${this.getProperty('volumes')}"
        .bindimensions="${this.getProperty('dimensions')}"
        .binlevel="${this.level}"
        .padding="${this.padding}"
        .volume="${this.levelVolume}"
        .volumes="${this.volumes}"
        ?medicated="${this.medicated}"
      ></bin-profile-svg>

      ${renderIf(!this.ignoreLatestLevel, html`
        <bin-latest-level .href="${this.href}" @levelUpdate="${this._latestLevelChanged}"></bin-latest-level>
      `)}
    `;
  }

  _entityChanged(entity) {
    super._entityChanged(entity);
    this.nodevice = isMonitoredWithNoDevice(this.entity?.properties);
  }

  _latestLevelChanged(e) {
    const { levelEntity, latestLevel, volume, status } = e.detail;
    const entity = levelEntity || latestLevel;
    if (!entity) {
      this.level = undefined;
      this.levelVolume = undefined;
      this.isStale = true;
      this.isOld = false;
      return;
    }
    this.isStale = this.levelfreshnessdate && moment(entity.properties.publishedAt).isBefore(this.levelfreshnessdate);
    this.isOld = status === 'old';
    this.level = this.isStale ? undefined : entity.properties;
    this.levelVolume = this.level ? volume : undefined;
  }
}

defineCustomElement('bin-profile', BinProfile);
